import { inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { set } from 'lodash-es';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { LocalStorageService } from '../../../core/services/local-storage.service';
import { SessionStorageService } from '../../../core/services/session-storage.service';
import { base64StringToObject, objectToBase64String } from '../../utils';
import { LU_STICKY_FILTERS_STORAGE_KEY, LuStickyFilter, LuStickyFilters, StorageService } from './sticky-filters.model';

@Injectable()
export class LuStickyFiltersService implements OnDestroy {
	private _stickyFilterOfCurrentPage: LuStickyFilter;
	private _localStorageService: StorageService = inject(LocalStorageService);
	private _sessionStorageService: StorageService = inject(SessionStorageService);
	private storageService: StorageService = this._localStorageService;
	private currentUrl: string;
	private destroy$: Subject<void> = new Subject<void>();
	private router = inject(Router);

	constructor() {
		this.listenRouterEvents();
	}

	setStorageType(storage: StorageService): void {
		this.storageService = storage;
	}

	get stickyFilterOfCurrentRoute(): LuStickyFilter {
		return this._stickyFilterOfCurrentPage;
	}
	setStickyFilterOfCurrentRoute(path: string): void {
		this._stickyFilterOfCurrentPage = this.getFilterByPath(path) || {};
	}

	getFilterByPath(path: string): LuStickyFilter {
		const currentRoute = this.router.url.split('/')[1];
		const stickyFiltersObject: LuStickyFilters = this.getStickyFilterObjectFromStorage();
		return stickyFiltersObject?.[currentRoute]?.[path];
	}

	saveFilter(path: string, modelName: string, data: any): void {
		const currentRoute = this.router.url.split('/')[1];
		const stickyFiltersObject: LuStickyFilters = this.getStickyFilterObjectFromStorage() || ({} as LuStickyFilters);
		set(stickyFiltersObject, [currentRoute, path, modelName], data);
		this.saveStickyFiltersObjectToStorage(stickyFiltersObject);
	}

	/**
	 * ModelName is optional, if it is not passed all filters on page will be deleted.
	 * If modelName is passed, only specific filter will be deleted.
	 *
	 * @param {string} path The key for all filter objects on specific page
	 * @param {string} modelName Optional param - the key for specific filter model on the page
	 */
	clearFilter(path: string, modelName?: string): void {
		const currentRoute = this.router.url.split('/')[1];
		const stickyFiltersObject: LuStickyFilters = this.getStickyFilterObjectFromStorage();
		if (!stickyFiltersObject || !stickyFiltersObject[currentRoute]) return;
		modelName && stickyFiltersObject[currentRoute][path]
			? delete stickyFiltersObject[currentRoute][path][modelName]
			: delete stickyFiltersObject[currentRoute][path];
		this.updateStorage(stickyFiltersObject);
	}

	/**
	 * Clear data persisted on both LocalStorage and SessionStorage
	 */
	clearAllFilters(): void {
		this._localStorageService.removeData(LU_STICKY_FILTERS_STORAGE_KEY);
		this._sessionStorageService.removeData(LU_STICKY_FILTERS_STORAGE_KEY);
	}

	private clearRouteFilters(route: string): void {
		const stickyFiltersObject: LuStickyFilters = this.getStickyFilterObjectFromStorage();
		if (!stickyFiltersObject || !stickyFiltersObject[route]) return;
		delete stickyFiltersObject[route];
		this.updateStorage(stickyFiltersObject);
	}

	private updateStorage(stickyFiltersObject: LuStickyFilters): void {
		const cleanFilterObject = this.removeEmptyObjects(stickyFiltersObject);
		if (!Object.keys(cleanFilterObject).length) {
			this.storageService.removeData(LU_STICKY_FILTERS_STORAGE_KEY);
		} else {
			this.saveStickyFiltersObjectToStorage(cleanFilterObject);
		}
	}

	/**
	 * It will iterate through all path and model keys and remove
	 * empty objects. It returns cleaned object.
	 *
	 * @param {LuStickyFilters} stickyFiltersObject - object to be cleaned
	 * @returns {LuStickyFilters} stickyFiltersObject - cleaned object
	 */
	private removeEmptyObjects(stickyFiltersObject: LuStickyFilters): LuStickyFilters {
		if (!stickyFiltersObject) return;
		Object.keys(stickyFiltersObject).forEach(route => {
			Object.keys(stickyFiltersObject[route]).forEach(path => {
				if (!Object.keys(stickyFiltersObject[route][path]).length) {
					delete stickyFiltersObject[route][path];
				}
			});
			if (!Object.keys(stickyFiltersObject[route]).length) {
				delete stickyFiltersObject[route];
			}
		});
		return stickyFiltersObject;
	}

	private getStickyFilterObjectFromStorage(): LuStickyFilters {
		const base64StringFilters = this.storageService.getData(LU_STICKY_FILTERS_STORAGE_KEY);
		if (!base64StringFilters) return;
		return base64StringToObject<LuStickyFilters>(base64StringFilters);
	}

	private saveStickyFiltersObjectToStorage(filters: LuStickyFilters): void {
		if (!filters) return;
		const base64StringFilters = objectToBase64String(filters);
		this.storageService.setData(LU_STICKY_FILTERS_STORAGE_KEY, base64StringFilters);
	}

	/**
	 * This method is responsible for listening to router events.
	 * It subscribes to the router events and filters out the events that are instances of NavigationStart or NavigationEnd and not related to the root URL.
	 *
	 * In the subscription:
	 * - If the event is an instance of NavigationStart, it checks if the current URL is different from the new URL. If it is, it clears the filters related to the current URL.
	 * - If the event is an instance of NavigationEnd, it updates the current URL to the new URL.
	 *
	 * This method is called in the constructor, so it starts listening to the router events as soon as an instance of the service is created.
	 *
	 * The subscription is automatically unsubscribed when the component is destroyed, thanks to the takeUntil operator.
	 */
	private listenRouterEvents(): void {
		this.router.events
			.pipe(
				takeUntil(this.destroy$),
				filter(event => (event instanceof NavigationStart || event instanceof NavigationEnd) && event.url !== '/')
			)
			.subscribe((event: NavigationStart | NavigationEnd) => {
				if (event instanceof NavigationStart) {
					const newUrl = event.url.split('/')[1];
					if (this.currentUrl && this.currentUrl !== newUrl) {
						this.clearRouteFilters(this.currentUrl);
					}
				} else if (event instanceof NavigationEnd) {
					this.currentUrl = event.url.split('/')[1];
				}
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
